const getPublicationImage = (slug, index) =>
  `https://picsum.photos/seed/${slug}-${index}/1200/800`;

export const publications = [
  {
    id: 1,
    title: 'Egyptian Capital Markets: Regulatory Updates 2026',
    category: 'CAPITAL MARKETS',
    date: '15 Jan 2026',
    summary:
      'An in-depth analysis of recent regulatory changes affecting capital markets in Egypt and their implications for investors.',
    heroImage: getPublicationImage('capital-markets', 1),
    sections: [
      {
        heading: 'Overview of the 2026 Regulatory Landscape',
        paragraphs: [
          'Egyptian capital markets have continued to evolve in response to global economic shifts and regional investment trends. The 2026 regulatory updates reflect a strong focus on transparency, investor protection, and alignment with international best practices.',
          'This publication summarizes the key regulatory changes introduced by the Financial Regulatory Authority (FRA), the Egyptian Exchange (EGX), and other relevant bodies, with a particular emphasis on disclosure obligations and governance standards.',
        ],
        image: getPublicationImage('capital-markets', 2),
      },
      {
        heading: 'Key Implications for Issuers and Investors',
        paragraphs: [
          'Issuers are now required to adopt more robust internal controls and reporting frameworks, with enhanced board oversight and more frequent public disclosures. These reforms are designed to reduce information asymmetry and support fair price discovery.',
          'For investors, the updated framework introduces additional mechanisms for redress and clearer guidelines around insider trading and market abuse. Foreign investors, in particular, may benefit from greater regulatory certainty and alignment with standards they are familiar with in other major markets.',
        ],
      },
    ],
  },
  {
    id: 2,
    title: 'M&A Transactions: Due Diligence Best Practices',
    category: 'M&A',
    date: '8 Jan 2026',
    summary:
      'Key considerations and best practices for conducting thorough due diligence in cross-border M&A transactions.',
    heroImage: getPublicationImage('ma-due-diligence', 1),
    sections: [
      {
        heading: 'Structuring an Effective Due Diligence Process',
        paragraphs: [
          'Successful M&A execution relies heavily on a disciplined and well-structured due diligence process. Buyers must develop a clear scope that prioritizes legal, regulatory, financial, and operational risks, tailored to the target\'s sector and geography.',
          'A cross-functional team—typically combining legal, financial, tax, and technical experts—should be assembled at an early stage. Clear workstreams, realistic timelines, and efficient information requests set the foundation for a smooth transaction.',
        ],
        image: getPublicationImage('ma-due-diligence', 2),
      },
      {
        heading: 'Common Red Flags and Mitigation Strategies',
        paragraphs: [
          'Frequent red flags in cross-border transactions include unclear ownership structures, unresolved regulatory inquiries, non-compliant employment practices, and weak data protection frameworks.',
          'Where issues are identified, parties can consider targeted contractual protections—such as specific indemnities, purchase price adjustments, or conditions precedent—to allocate risks appropriately while preserving deal value.',
        ],
      },
    ],
  },
  {
    id: 3,
    title: 'Data Protection Compliance: GDPR and Local Regulations',
    category: 'PRIVACY',
    date: '2 Jan 2026',
    summary:
      'Understanding the intersection of GDPR requirements with Egyptian data protection laws and compliance strategies.',
    heroImage: getPublicationImage('data-protection', 1),
    sections: [
      {
        heading: 'Navigating Overlapping Regulatory Regimes',
        paragraphs: [
          'Organisations operating in or with the EU must frequently comply with both GDPR and local data protection laws. In Egypt, the data protection framework imposes consent, notification, and localisation requirements that must be harmonised with GDPR obligations.',
          'A practical compliance approach focuses on mapping data flows, identifying lawful bases for processing, and ensuring that cross-border transfers are supported by appropriate contractual and technical safeguards.',
        ],
        image: getPublicationImage('data-protection', 2),
      },
      {
        heading: 'Building a Sustainable Compliance Programme',
        paragraphs: [
          'Beyond initial gap assessments, organisations should invest in sustainable governance structures—such as clear data ownership, regular training, and incident response procedures.',
          'Embedding privacy-by-design in new products and services helps reduce long-term compliance costs while strengthening trust with customers, employees, and regulators.',
        ],
      },
    ],
  },
  {
    id: 4,
    title: 'Fintech Licensing in Egypt: A Practical Guide',
    category: 'FINTECH',
    date: '28 Dec 2025',
    summary:
      'Comprehensive guide to navigating the fintech licensing process in Egypt, including regulatory requirements and timelines.',
    heroImage: getPublicationImage('fintech-licensing', 1),
    sections: [
      {
        heading: 'Regulatory Framework for Fintech Activities',
        paragraphs: [
          'Egypt has introduced a dedicated regulatory framework to support innovation in digital payments, crowdfunding, robo-advisory services, and other fintech verticals. These rules seek to strike a balance between market development and consumer protection.',
          'Understanding which authority regulates a specific activity—the Central Bank of Egypt, the Financial Regulatory Authority, or other sectoral regulators—is the first step in designing a viable licensing strategy.',
        ],
        image: getPublicationImage('fintech-licensing', 2),
      },
      {
        heading: 'Licensing Roadmap and Key Considerations',
        paragraphs: [
          'A typical licensing roadmap includes pre-filing consultations, preparation of business plans and policies, fulfilment of capital and governance requirements, and post-licensing reporting.',
          'Applicants should anticipate questions about cybersecurity, outsourcing arrangements, anti-money laundering controls, and consumer redress mechanisms, and address them proactively in their submissions.',
        ],
      },
    ],
  },
  {
    id: 5,
    title: 'Real Estate Investment Structures in Free Zones',
    category: 'REAL ESTATE',
    date: '20 Dec 2025',
    summary:
      'Exploring optimal investment structures for real estate projects within Egyptian free zones and tax implications.',
    heroImage: getPublicationImage('real-estate-free-zones', 1),
    sections: [
      {
        heading: 'Advantages of Free Zone Structures',
        paragraphs: [
          'Free zones can offer attractive incentives for real estate investors, including customs exemptions, streamlined licensing, and, in some cases, tax benefits. Selecting the right zone and vehicle is critical to maximising these advantages.',
          'Investors should consider the interplay between free zone regulations, national investment laws, and sector-specific rules—particularly where projects involve utilities, tourism, or industrial activities.',
        ],
        image: getPublicationImage('real-estate-free-zones', 2),
      },
      {
        heading: 'Governance, Exit, and Financing Considerations',
        paragraphs: [
          'From a governance perspective, joint ventures and fund structures must address decision-making thresholds, reserved matters, and dispute resolution mechanisms.',
          'Financing arrangements should account for security packages over project assets, cash flows, and shares, as well as any restrictions on enforcement or transfer within the relevant free zone.',
        ],
      },
    ],
  },
  {
    id: 6,
    title: 'Employment Law Updates: Remote Work Compliance',
    category: 'EMPLOYMENT',
    date: '12 Dec 2025',
    summary:
      'Recent updates to Egyptian employment law regarding remote work arrangements and compliance requirements.',
    heroImage: getPublicationImage('remote-work-compliance', 1),
    sections: [
      {
        heading: 'Formalising Remote Work Arrangements',
        paragraphs: [
          'Remote and hybrid working models have become a permanent feature of many organisations. Egyptian employment law now places greater emphasis on formalising these arrangements through clear policies and contractual terms.',
          'Key topics include working hours, health and safety obligations, equipment provision, and the treatment of overtime and allowances in remote settings.',
        ],
        image: getPublicationImage('remote-work-compliance', 2),
      },
      {
        heading: 'Managing Performance, Privacy, and Culture',
        paragraphs: [
          'Employers are increasingly using digital tools to monitor productivity and engagement. These tools must be deployed in a way that respects employee privacy and complies with data protection rules.',
          'Maintaining organisational culture in a remote environment requires intentional communication, inclusive leadership practices, and regular opportunities for in-person or virtual connection.',
        ],
      },
    ],
  },
];

export const getPublicationById = (id) =>
  publications.find((pub) => String(pub.id) === String(id));

